import { Credit, WebMapServiceImageryProvider } from 'cesium';

// NVE moved their services: the old `nve.geodataonline.no` host no longer resolves at all.
// The hazard datasets live on `kart.nve.no/enterprise`, the grid ones on `gis3.nve.no/map`,
// and the two deployments carry different layer names for the same data — so check
// GetCapabilities before assuming a name carries over. Verified 2026-09-11.

// Distribusjonsnett-lag fra Nettanlegg WMS
const distributionWMS = {
	url: 'https://gis3.nve.no/map/services/Nettanlegg3/MapServer/WMSServer?',
	layers: 'Distribusjonsnett',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Sentralnett, Ragionalnett and Distribusjonsnett Nettanlegg WMS
const nettanleggWMS = {
	url: 'https://gis3.nve.no/map/services/Nettanlegg3/MapServer/WMSServer?',
	layers: ['Regionalnett', 'Sentralnett', 'Distribusjonsnett'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Transformatorstasjoner-lag fra Nettanlegg WMS
const transformerWMS = {
	url: 'https://gis3.nve.no/map/services/Nettanlegg3/MapServer/WMSServer?',
	layers: [
		'Transformatorstasjoner_storre_eller_lik_300kV',
		'Transformatorstasjoner_mindre_enn_300kV'
	].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Master og stolper-lag fra Nettanlegg WMS
const masterOgStolperWMS = {
	url: 'https://gis3.nve.no/map/services/Nettanlegg3/MapServer/WMSServer?',
	layers: 'Master_og_stolper',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Områdekonsesjoner-lag fra Nettanlegg WMS
const omradekonsesjonaererWMS = {
	url: 'https://gis3.nve.no/map/services/Nettanlegg3/MapServer/WMSServer?',
	layers: 'Omradekonsesjonaerer',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Matrikkelen Enkel-lag fra Matrikkelen WMS
const cadastreSimpleWMS = {
	url: 'https://wms.geonorge.no/skwms1/wms.matrikkelkart?',

	layers: 'matrikkelkart',
	parameters: {
		transparent: true,
		format: 'image/png'
	}
} as WebMapServiceImageryProvider.ConstructorOptions;

/**
 * What a click on Matrikkel asks about. The drawn layer is the `matrikkelkart` GROUP, which has
 * no data source of its own — querying it makes MapServer answer
 * `msShapefileOpen(): Unable to access file`, so the children have to be named instead.
 *
 * `teig` is the matrikkelnummer LABEL layer and holds nothing at normal zoom; the geometry the
 * user actually clicks is the boundaries, buildings and addresses. Ordered least to most
 * specific, so the nearest-first ranking puts a building or address above the boundary it sits
 * inside.
 */
const CADASTRE_QUERY_LAYERS = [
	'eiendomsgrense',
	'anleggsprojeksjonsflate',
	'teig',
	'bygning_symbol',
	'adresse'
].join(',');

const CULTURAL_HERITAGE_META_LAYER_NAME = 'Kulturminner';

/**
 * Cesium still decides WHICH layer a click belongs to, but the GetFeatureInfo request for
 * these five is built by `wms-feature-info.ts`: Cesium queries the 256 px tile under the
 * cursor, so MapServer's pixel-based tolerance shrinks as you zoom in and all but
 * pixel-perfect clicks miss. That module sizes its own query window instead, and is
 * installed onto the providers via `IMetaMapLayer.installPickFeatures`.
 */

// Kulturminner WMS.
// Riksantikvaren retired the combined `wms/kulturminner` mapfile and split it into
// one service per dataset. LAYERS can only be comma-joined within a single WMS
// endpoint, so the building, kulturmiljø and brannvern layers that used to ride
// along here now live in the sibling providers below.
// The parent group layer `Kulturminner` is equivalent to these five children.
const culturalHeritageWMS = {
	url: 'https://kart.ra.no/wms/kulturminner2?',
	layers: [
		'Lokaliteter',
		'Enkeltminner',
		'Sikringssoner',
		'Lokalitetsikoner',
		'Enkeltminneikoner'
	].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Riksantikvaren')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Kulturmiljøer, split out of the old combined Kulturminner WMS.
const culturalEnvironmentsWMS = {
	url: 'https://kart.ra.no/wms/kulturmiljoer?',
	layers: [
		'Fredede_kulturmiljoer',
		'Kommunale_kulturmiljoer',
		'Kulturmiljo_og_landskap_av_nasjonal_interesse',
		'Regionale_kulturmiljo',
		'Verdensarv'
	].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Riksantikvaren')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Freda bygninger, split out of the old combined Kulturminner WMS.
const listedBuildingsWMS = {
	url: 'https://kart.ra.no/wms/freda_bygninger?',
	layers: 'Freda_bygninger',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Riksantikvaren')
} as WebMapServiceImageryProvider.ConstructorOptions;

// SEFRAK-bygninger, split out of the old combined Kulturminner WMS.
const sefrakBuildingsWMS = {
	url: 'https://kart.ra.no/wms/sefrak?',
	layers: 'SEFRAKBygninger',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Riksantikvaren')
} as WebMapServiceImageryProvider.ConstructorOptions;

// Brannvern, split out of the old combined Kulturminner WMS.
// `TetteTrehusmiljoer` is the old `VerneverdigTetteTrehusmiljoer`, renamed.
const fireProtectionWMS = {
	url: 'https://kart.ra.no/wms/brannvern?',
	layers: ['Brannsmitteomraader', 'TetteTrehusmiljoer'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Riksantikvaren')
} as WebMapServiceImageryProvider.ConstructorOptions;

/**
 * Every Riksantikvaren service behind the single "Kulturminner" toggle. Drawn in
 * order, so the icon layers in `culturalHeritageWMS` end up beneath the building
 * and brannvern overlays.
 */
const culturalHeritageWMSGroup = [
	culturalHeritageWMS,
	culturalEnvironmentsWMS,
	listedBuildingsWMS,
	sefrakBuildingsWMS,
	fireProtectionWMS
];

const ar5ArealtypeWMS = {
	url: 'https://wms.nibio.no/cgi-bin/ar5?',
	layers: 'Arealtype',
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norsk institutt for bioøkonomi')
} as WebMapServiceImageryProvider.ConstructorOptions;

const quickClayWMS = {
	url: 'https://kart.nve.no/enterprise/services/SkredKvikkleire2/MapServer/WMSServer?',
	layers: ['KvikkleireKartlagtOmrade', 'KvikkleireRisiko', 'KvikkleireFaregrad'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

const skredJordFlomAktR1WMS = {
	url: 'https://kart.nve.no/enterprise/services/SkredJordFlomAktR1/MapServer/WMSServer?',
	layers: ['Potensielt_Jord-Flomskredfare', 'Jord-Flomskred_Aktsomhetsomrader'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

const nveFlomaktsomhetWMS = {
	url: 'https://kart.nve.no/enterprise/services/Flomaktsomhet/MapServer/WMSServer?',
	layers: ['FlomAktsomhet_Dekning', 'Flom_aktsomhetsomrade', 'MaksimalVannstandstigning'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

const nveSkredSnoAktRWMS = {
	url: 'https://kart.nve.no/enterprise/services/SkredSnoSteinAkt/MapServer/WMSServer?',
	layers: ['IkkeKartlagtOmrade', 'KartlagtOmrade', 'Aktsomhetsomrade'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

const nveSkredSteinAktRRWMS = {
	url: 'https://kart.nve.no/enterprise/services/SkredSteinAktR/MapServer/WMSServer?',
	layers: ['Utlopsomrade', 'Utlosningsomrade', 'Steinsprang-AktsomhetOmrader'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges vassdrags- og energidirektorat')
} as WebMapServiceImageryProvider.ConstructorOptions;

const nguGranada5WMS = {
	url: 'https://geo.ngu.no/mapserver/GranadaWMS5?',
	layers: ['Borehull_dyp_til_fjell'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Norges geologiske undersøkelse')
} as WebMapServiceImageryProvider.ConstructorOptions;

const planomraadeWMS = {
	url: 'https://wms.geonorge.no/skwms1/wms.planomraade?',
	layers: ['PLANOMRADE_WMS'].join(','),
	parameters: {
		transparent: true,
		format: 'image/png'
	},
	credit: new Credit('Kartverket')
} as WebMapServiceImageryProvider.ConstructorOptions;

/**
 * Util for simple import (into other files) of integrated WMS imagery layers.
 */
const getWMSImageryMetaProviders = () => {
	return {
		distributionWMS,
		transformerWMS,
		nettanleggWMS,
		masterOgStolperWMS,
		cadastreSimpleWMS,
		omradekonsesjonaererWMS,
		culturalHeritageWMSGroup,
		ar5ArealtypeWMS,
		quickClayWMS,
		skredJordFlomAktR1WMS,
		nveFlomaktsomhetWMS,
		nveSkredSnoAktRWMS,
		nveSkredSteinAktRRWMS,
		nguGranada5WMS,
		planomraadeWMS
	};
};

export { getWMSImageryMetaProviders, CULTURAL_HERITAGE_META_LAYER_NAME, CADASTRE_QUERY_LAYERS };
